import { TableCell, TableRow, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    roiColorsState,
    roisVisibilityState,
} from '~/state/atoms/structureSet/roiAtoms';
import { tableScrollbarStyles } from '../tableScrollbarStyles';
import DoseStatisticsTableHead from './DoseStatisticsTableHead';
import DoseStatisticsTableRow from './DoseStatisticsTableRow';
import { CurveData } from '~/domain/patient/dvh';
export type Order = 'asc' | 'desc';

function compareValues<T>(
    order: Order,
    orderBy: keyof T
): (a: T, b: T) => number {
    return (a, b) => {
        const valueA = a[orderBy];
        const valueB = b[orderBy];

        if (typeof valueA === 'number' && typeof valueB === 'number') {
            return order === 'asc' ? valueA - valueB : valueB - valueA;
        }

        if (typeof valueA === 'string' && typeof valueB === 'string') {
            return order === 'asc'
                ? valueA.localeCompare(valueB)
                : valueB.localeCompare(valueA);
        }

        return 0; // Fallback for mixed or undefined types
    };
}

const createDoseStatisticsRow = (dvh: CurveData, idx: number) => ({
    id: idx,
    color: dvh.color,
    structure: dvh.name,
    minDoseGy: dvh.doseMinGy,
    maxDoseGy: dvh.doseMaxGy,
    meanDoseGy: dvh.doseMeanGy,
    roiId: dvh.roiId,
});

export type DoseStatisticsRow = ReturnType<typeof createDoseStatisticsRow>;

const DoseStatisticsComponent = ({
    dvhCurves,
}: {
    dvhCurves?: CurveData[];
}) => {
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] =
        useState<keyof DoseStatisticsRow>('structure');
    const handleRequestSort = (
        _event: React.MouseEvent<unknown>,
        property: keyof DoseStatisticsRow
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const rows = useMemo(() => {
        const allRows = dvhCurves?.map(createDoseStatisticsRow) ?? [];
        return [...allRows].sort(compareValues(order, orderBy));
    }, [order, orderBy, dvhCurves]);

    const roiIds = useMemo(() => rows.map((row) => row.roiId), [rows]);
    const roiVisibilityMap = useRecoilValue(roisVisibilityState(roiIds));
    const roiColorMap = useRecoilValue(roiColorsState(roiIds));

    const filteredRows = useMemo(
        () =>
            rows
                .map((row) => ({
                    ...row,
                    color: roiColorMap.get(row.roiId) ?? row.color,
                }))
                .filter((row) => roiVisibilityMap.get(row.roiId)),
        [rows, roiVisibilityMap, roiColorMap]
    );

    return (
        <TableContainer
            sx={{
                width: '100%',
                maxHeight: '100%',
                height: '100%',
                overflow: 'auto',
                overscrollBehavior: 'contain',
                ...tableScrollbarStyles,
            }}
        >
            <Table size={'small'} stickyHeader>
                <DoseStatisticsTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                />
                <TableBody>
                    {filteredRows.length > 0 ? (
                        filteredRows.map((row) => (
                            <DoseStatisticsTableRow key={row.id} row={row} />
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ p: 2 }}>
                                <Typography
                                    variant="body1"
                                    color="textSecondary"
                                >
                                    No visible structures with dose data
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default DoseStatisticsComponent;
